import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import useHydratedParams from '../../../lib/hooks/useHydratedParams'
import { SizeChart } from '../../../types/graphql'

export type GenderKey = 'mens' | 'womens'
export type UseSizeChartOpts = {
  [key in GenderKey]: SizeChart[]
}

function isValidGender(str: string | undefined): str is GenderKey {
  return str === 'mens' || str === 'womens'
}

/**
 * Custom hook for state & URL management for the standalone Size Chart page.
 *
 * @param props
 * @param props.mens - An array of size charts associated with "Men's".
 * @param props.womens - An array of size charts associated with "Men's".
 */
export function useSizeChart({ mens: mensSizeCharts, womens: womensSizeCharts }: UseSizeChartOpts) {
  const router = useRouter()
  // must use `useHydratedParams` to ensure that params are rehydrated on the client
  // (`gender` & `label` params are not read on the server)
  const params = useHydratedParams()

  const selectedGender = params.gender && isValidGender(params.gender) ? params.gender : 'womens' // Default to 'womens'
  const label = params.label ?? womensSizeCharts[0]?.navLabel ?? undefined // Default to the first womens size chart

  // The current gendered charts (either `mensSizeCharts` or `womensSizeCharts`)
  const selectedGenderedCharts = useMemo<SizeChart[]>(
    () => (selectedGender === 'mens' ? mensSizeCharts : womensSizeCharts),
    [mensSizeCharts, womensSizeCharts, selectedGender]
  )

  // Derive the currently selected size chart
  const selectedChart = useMemo<SizeChart | undefined>(() => {
    return (
      selectedGenderedCharts.find(({ navLabel }) => navLabel === label) ??
      selectedGenderedCharts[0] ??
      undefined
    )
  }, [label, selectedGenderedCharts])

  // Update the currently selected size chart to a specific gender/chart label
  const setSelectedChart = useCallback(
    (gender: GenderKey, label: string) => {
      const chart = selectedGenderedCharts.find(({ navLabel }) => navLabel === label)

      if (!chart) return
      router.push(
        {
          query: { ...router.query, label, gender },
        },
        undefined,
        { shallow: true }
      )
    },
    [selectedGenderedCharts, router]
  )

  return {
    selectedGender,
    selectedChart,
    setSelectedChart,
  }
}
